import {
  Box,
  Flex,
  Text,
  IconButton,
  useColorModeValue,
  useDisclosure,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import {
  BackIcon,
  CodeQuickIcon,
  CommentIcon,
  SheetEyeIcon,
} from '../../../constants/IconData';
import AppColors from '../../../constants/AppColors';
import { FONT_FAMILY } from '../../../constants/Theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { AuditSheetContext } from '../../../providers/AuditSheetProvider';
import { format } from 'date-fns';
import AuditSheetFileViewer from './AuditSheetFileViewer';
import NotesDrawer from './NotesDrawer';
import { useBoolean } from '@laxmimanogna/code-quick-components';
import BatchFileModal from '../../dashboard/components/BatchFileModal';
import BatchFileViewer from '../../../components/pdf_viewer/BatchFileViewer';
import ROUTES from '../../../constants/Routes';
import { AuthContext } from '../../../providers/AuthProvider';

export default function Topbar({
  updateAuditSheet,
  view,
  lastSaved,
  onChange,
  onRebuttalClick,
  batchId,
  batchName,
  rendererId,
  rendererOptions,
  selectedRenderer,
  setSelectedRenderer,
  onOpenNotes,
  notesDrawerRef,
  filterByDate = false,
  filterSheetsByDate,
  removeDateFilter
}) {
  const navigate = useNavigate();
  // context
  const auditSheetContext = useContext(AuditSheetContext);
  const authContext = useContext(AuthContext);
  const { currentUpload, batchRebuttalSummary } = auditSheetContext;

  // other hooks
  const { isOpen, onToggle } = useDisclosure();
  const { state } = useLocation();
  // states
  const [currentBatch, setCurrentBatch] = useState({});
  const [batchFileData, setBatchFileData] = useState(null);
const [isFetchingBatch, setIsFetchingBatch] = useState(false);
  // boolean
  const [isModalOpen, imoState] = useBoolean(false);

  const handleSelectedRendererChange = (event) => {
    setSelectedRenderer(event.target.value);
  };
  

  const getTableData = async () => {
    const res = await auditSheetContext.getFileObjectByBatchId(batchId, rendererId);
    return res;
  };

  const handleOpenBatchViewer = async () => {
  if (batchFileData) { imoState.on(); return; }  // already fetched, reuse same reference
  setIsFetchingBatch(true);
  try {
    const res = await auditSheetContext.getFileObjectByBatchId(batchId, rendererId);
    setBatchFileData(res);
    imoState.on();
  } finally {
    setIsFetchingBatch(false);
  }
};

useEffect(() => { setBatchFileData(null); }, [batchId, rendererId]); // reset on route change

  useEffect(() => {
    if (batchId) {
      setCurrentBatch({upload_id: batchId});
    }
    
  }, [batchId])

  async function onBack() {
    if (!view) {
      onChange(true);
      await updateAuditSheet();
      onChange(false);
    }
    navigate(-1);
  }

  async function onLogoClick() {
    if (!view) {
      onChange(true);
      await updateAuditSheet();
      onChange(false);
    }
    navigate(ROUTES.DASHBOARD);
  }

  const [checked, setChecked] = useState(false);


  return (
    <Box w={'100%'}>
      <Flex
        bg={AppColors.secondary}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
        position="relative"
        // _hover={{
        //   transition: 'all .1s ease',
        //   bgColor: AppColors.secondary,
        //   color: AppColors.white,
        // }}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex flex={{ base: 1 }} alignItems={'center'}>
          <Box pl={4}>
            <CodeQuickIcon
              onClick={onLogoClick}
              style={{ height: 40, cursor: 'pointer' }}
            />
          </Box>
          <BackIcon
            style={{
              marginLeft: 20,
              width: 40,
              height: 40,
              cursor: 'pointer',
              display: 'inline-block',
            }}
            onClick={onBack}
          />
          <HStack spacing={4} ml={6} alignItems={'center'}>
            <Text
              display={{ base: 'none', md: 'block' }}
              color={'white'}
              fontSize={'xl'}
              fontWeight={'bold'}
            >
              {batchId ? (batchName || batchId) : currentUpload.upload_id}
            </Text>
            {!batchId && (
              <select value={selectedRenderer} onChange={handleSelectedRendererChange}
                style={{ borderRadius: '4px', padding: '2px 6px', fontSize: '13px' }}
              >
                {Object.entries(rendererOptions).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            )}
            {filterByDate && (
              <label style={{ color: 'white', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    if (checked) { filterSheetsByDate(); } else { removeDateFilter(); }
                    setChecked(!checked);
                  }}
                />
                Remove Date Filter
              </label>
            )}
            {/* Individual chart view — single upload status */}
            {!batchId && (currentUpload.status === 'CLIENT REBUTTAL' || currentUpload.status === 'REBUTTAL IN PROGRESS') && (
              <Box px={3} py={1} borderRadius='md' bg='rgba(26,115,232,0.85)' border='1px solid rgba(255,255,255,0.3)'>
                <Text fontSize='xs' fontWeight='semibold' color='white' whiteSpace='nowrap'>
                  In QA Review
                </Text>
              </Box>
            )}
          </HStack>
          {/* Batch view — per-chart rebuttal summary */}
          {batchId && batchRebuttalSummary?.count > 0 && (
            <Tooltip
              label={batchRebuttalSummary.charts.map(c => c.name).join('\n')}
              bg='white'
              color='gray.700'
              hasArrow
              whiteSpace='pre-line'
              placement='bottom-end'
            >
              <Box ml={4} px={3} py={1} borderRadius='md' bg='rgba(217,119,6,0.9)' border='1px solid rgba(255,255,255,0.3)' cursor='default'>
                <Text fontSize='xs' fontWeight='semibold' color='white' whiteSpace='nowrap'>
                  {batchRebuttalSummary.count} chart{batchRebuttalSummary.count > 1 ? 's' : ''} in review
                </Text>
              </Box>
            </Tooltip>
          )}
        </Flex>

        <HStack alignItems={'center'} spacing={2} gap={2} mr={3}>
          <Text
            fontSize={'sm'}
            fontFamily={FONT_FAMILY.ptSans}
            color={'white'}
            fontWeight="bold"
          >
            {lastSaved
              ? ` Last saved ${format(
                  new Date(lastSaved),
                  'MMM dd, yyyy'
                )} at ${format(new Date(lastSaved), 'p')}`
              : null}
          </Text>
          <Tooltip hasArrow label="Add rebuttal" bg="white" color="black">
            <Box display="inline-block">
              <CommentIcon
                style={{ width: 40, height: 40, cursor: 'pointer' }}
                onClick={() => onRebuttalClick()}
              />
            </Box>
          </Tooltip>
          {Object.keys(currentUpload).length ? (
            <AuditSheetFileViewer auditData={currentUpload} chart_id={currentUpload?.upload_id}/>
          ) : (
            <SheetEyeIcon
              style={{ cursor: 'pointer', height: 40, width: 40 }}
              onClick={handleOpenBatchViewer}
            />
          )}
          <NotesDrawer onOpenNotes={onOpenNotes} drawerRef={notesDrawerRef} />
        
        </HStack>
      </Flex>
      <BatchFileViewer
        isOpen={isModalOpen}
        onClose={imoState.off}
        batchFiles={batchFileData ?? []}
      />
    </Box>
  );
}
